import {forwardRef, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import useDebounce from 'ahooks/es/useDebounce';

import {Topic} from '../../../topic';
import {useGetTopics} from '../../../topic/apiHooks';

type Props = {
  value: Topic | null;
  onChange: (value: Topic | null) => void;
  label?: string;
  error?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  categoryId?: string;
  subCategoryId?: string;
};

export const TopicSelect = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      label,
      error,
      fullWidth,
      disabled,
      categoryId,
      subCategoryId,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, {wait: 400});

    const {data, isPending} = useGetTopics({
      page: 1,
      pageSize: 50,
      searchQuery: debouncedSearch || undefined,
      categoryId: categoryId || undefined,
      subCategoryIds: subCategoryId || undefined,
    });

    const options = data?.items ?? [];

    // Ensure the currently selected topic is always in the list
    const isValueInOptions = value
      ? options.some(o => o.id === value.id || o.topicId === value.topicId)
      : true;
    const optionsWithSelected = isValueInOptions
      ? options
      : [value!, ...options];

    return (
      <Autocomplete
        ref={ref}
        value={value}
        inputValue={inputValue}
        options={optionsWithSelected}
        loading={isPending}
        disabled={disabled}
        fullWidth={fullWidth}
        filterOptions={x => x}
        getOptionLabel={option => option.name || option.topicId}
        isOptionEqualToValue={(option, val) =>
          option.topicId === val.topicId || option.id === val.id
        }
        onInputChange={(_, newInput, reason) => {
          setInputValue(newInput);
          if (reason === 'input') setSearchQuery(newInput);
          else setSearchQuery('');
        }}
        onChange={(_, newValue) => onChange(newValue)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isPending && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  },
);
